import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Orthotimer.module.css";
import { useOrthotimer } from "../../context/OrthotimgerContext";
import { isTestUser } from "../../utils/testUser"; // TEMPORÄRER TESTZUGANG

const SALUTATION_TO_GESCHLECHT = {
  "de609b657f5a473298bab08c6df6d35a": "männlich",
  "e04001f133214426b2c7c2155f2af10a": "weiblich",
  "37e91b8cad6647d187a099eda9aaaf48": "divers",
  "ea5064df7ac24d1e9d6455fecfd31836": "Keine Angabe",
};
const mapSalutationToGeschlecht = (id) => SALUTATION_TO_GESCHLECHT[id] ?? null;

const SENSOR_LABELS = ["Sensor 1", "Sensor 2", "Sensor 3"];
const SENSOR_COLORS = ["#0ba1e2", "#f2994a", "#27ae60"];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon…Sun (getDay() indices)
const WEEKDAY_NAMES = [
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
  "Sonntag",
];

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// Parses date string — English "M/D/YYYY" or German "DD.MM.YYYY".
function parseDate(dateStr) {
  const s = dateStr || "";
  if (s.includes("/")) {
    const [m, d, y] = s.split("/");
    return { day: parseInt(d, 10), month: parseInt(m, 10), year: parseInt(y, 10) };
  }
  if (s.includes(".")) {
    const [d, m, y] = s.split(".");
    return { day: parseInt(d, 10), month: parseInt(m, 10), year: parseInt(y, 10) };
  }
  return { day: NaN, month: NaN, year: NaN };
}

// Parses time string — English "H:MM AM/PM" (12h) or German "HH:MM" (24h).
function parseTime(timeStr) {
  const s = timeStr || "";
  const ampm = / (AM|PM)$/i.exec(s);
  if (ampm) {
    const [hStr, mStr] = s.replace(/ (AM|PM)$/i, "").split(":");
    let hour = parseInt(hStr, 10);
    const minute = parseInt(mStr, 10);
    if (ampm[1].toUpperCase() === "AM" && hour === 12) hour = 0;
    if (ampm[1].toUpperCase() === "PM" && hour !== 12) hour += 12;
    return { hour, minute };
  }
  // 24h format (German)
  const parts = s.split(":");
  if (parts.length >= 2) {
    return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1], 10) };
  }
  return { hour: NaN, minute: NaN };
}

// Parses the "Serial;ReadingNr;Date(UTC);Time(UTC);Value" export (M/D/YYYY, H:MM AM/PM).
// Returns { rows, sensorSerial, skippedCount } — never throws on individual bad rows.
function parseCsvText(text) {
  // Binary / non-text check: control characters other than tab, CR, LF in the first 2 KB
  if (/[\x00-\x08\x0E-\x1F\x7F]/.test(text.slice(0, 2048))) {
    throw new Error("Die Datei enthält keine lesbaren Textdaten – möglicherweise handelt es sich um eine Binärdatei.");
  }

  const lines = text
    .split(/\r\n|\n|\r/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) throw new Error("Die Datei enthält keine Datenzeilen (nur Kopfzeile oder leer).");

  const header = lines[0].split(";").map((h) => h.trim().toLowerCase());
  const col = {
    serial: header.findIndex((h) => h === "serial"),
    date:   header.findIndex((h) => h.startsWith("date")),
    time:   header.findIndex((h) => h.startsWith("time")),
    value:  header.findIndex((h) => h === "value"),
  };

  // Required-headers check — whole file is unusable if these are absent
  const missing = ["serial", "date", "time", "value"].filter((k) => col[k] === -1);
  if (missing.length > 0) {
    throw new Error(`CSV-Pflichtfelder fehlen: ${missing.join(", ")}. Erwartet: Serial;…;Date(UTC);Time(UTC);Value`);
  }

  const at = (cols, key, fallback) =>
    (col[key] >= 0 ? cols[col[key]] : cols[fallback]) ?? "";

  const rows = [];
  let sensorSerial = null;
  let skippedCount = 0;

  for (let i = 1; i < lines.length; i++) {
    try {
      const cols = lines[i].split(";");
      if (cols.length < 5) { skippedCount++; continue; }

      const serial   = at(cols, "serial", 0).trim();
      const dateStr  = at(cols, "date",   2).trim();
      const timeStr  = at(cols, "time",   3).trim();
      const valueStr = at(cols, "value",  4).trim();

      const { day, month, year } = parseDate(dateStr);
      const { hour, minute }     = parseTime(timeStr);
      const value = parseFloat(valueStr.replace(",", "."));

      if ([day, month, year, hour, minute, value].some(Number.isNaN)) { skippedCount++; continue; }
      if (!sensorSerial && serial) sensorSerial = serial;

      const dateObj = new Date(year, month - 1, day, hour, minute);
      const dateKey = `${String(year).padStart(4,"0")}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
      rows.push({ dateObj, dateKey, hourDecimal: hour + minute / 60, value });
    } catch (_) {
      // Unexpected error on this row — skip it and keep going
      skippedCount++;
    }
  }

  rows.sort((a, b) => a.dateObj - b.dateObj);

  // Auto-detect sample interval from first two rows (in hours). Fallback to 0.25h (15 min).
  let sampleHours = 0.25;
  if (rows.length >= 2) {
    const diffMs = rows[1].dateObj - rows[0].dateObj;
    if (diffMs > 0) sampleHours = diffMs / 3600000;
  }

  return { rows, sensorSerial, skippedCount, sampleHours };
}

function weekdayAverages(dailyHours, dailyWeekday) {
  const sums   = new Array(7).fill(0);
  const counts = new Array(7).fill(0);
  for (const [key, hrs] of dailyHours.entries()) {
    const wd = dailyWeekday.get(key);
    if (wd === undefined) continue;
    sums[wd]   += hrs;
    counts[wd] += 1;
  }
  // Return null for weekdays with no data (matches clinical expectation — shows "–")
  return sums.map((sum, i) => (counts[i] ? sum / counts[i] : null));
}

function computeSensorStats(rows, threshold, sampleHours = 0.25) {
  const dailyHours   = new Map();
  const dailyWeekday = new Map();
  let totalHours  = 0;
  let sessionCount = 0;
  let prevWorn    = 0;
  let morning     = 0;
  let evening     = 0;
  let night       = 0;

  rows.forEach((r) => {
    const worn = r.value > threshold;
    if (!dailyHours.has(r.dateKey))   dailyHours.set(r.dateKey, 0);
    if (!dailyWeekday.has(r.dateKey)) dailyWeekday.set(r.dateKey, r.dateObj.getDay());

    if (worn) {
      dailyHours.set(r.dateKey, dailyHours.get(r.dateKey) + sampleHours);
      totalHours += sampleHours;
      const h = r.hourDecimal;
      if      (h >= 7  && h < 17)  morning += sampleHours;
      else if (h >= 17 && h < 22)  evening += sampleHours;
      else                          night   += sampleHours;
      // Count put-on events (0→1 transition). Starts at 0 — correct unlike MATLAB.
      if (!prevWorn) sessionCount += 1;
    }
    prevWorn = worn ? 1 : 0;
  });

  let notWornDays = 0;
  for (const hrs of dailyHours.values()) if (hrs === 0) notWornDays += 1;

  const durationDays = dailyHours.size;
  const wornDays = durationDays - notWornDays;
  const avgHoursPerWornDay = durationDays > 0 ? totalHours / durationDays : 0;

  return {
    totalHours,
    sessionCount,
    morning,
    evening,
    night,
    dailyHours,
    dailyWeekday,
    durationDays,
    notWornDays,
    wornDays,
    avgHoursPerWornDay,
    weekdayAvg: weekdayAverages(dailyHours, dailyWeekday),
  };
}

function buildCombined(sensorStats) {
  const present = sensorStats.filter(Boolean);
  const dateKeySet = new Set();
  present.forEach((s) => {
    for (const key of s.dailyHours.keys()) dateKeySet.add(key);
  });
  const sortedDates = Array.from(dateKeySet).sort();

  const chartData = sortedDates.map((key) => {
    const entry = { date: key };
    let daySum = 0;
    sensorStats.forEach((s, i) => {
      const hrs = s ? s.dailyHours.get(key) || 0 : 0;
      entry[`s${i + 1}`] = hrs;
      daySum += hrs;
    });
    entry.total = daySum;
    return entry;
  });

  const combinedTotalHours   = present.reduce((sum, s) => sum + s.totalHours,   0);
  const combinedSessionCount = present.reduce((sum, s) => sum + s.sessionCount, 0);

  // Calendar span: every day from the earliest to the latest date in any file,
  // so days with no sensor data are counted as not worn rather than ignored.
  const msPerDay = 86400000;
  const firstMs  = new Date(sortedDates[0]     + "T00:00:00").getTime();
  const lastMs   = new Date(sortedDates[sortedDates.length - 1] + "T00:00:00").getTime();
  const durationDays = sortedDates.length > 0
    ? Math.round((lastMs - firstMs) / msPerDay) + 1
    : 0;

  const avgPerDay = durationDays ? combinedTotalHours / durationDays : 0;

  const MIN_DAILY_HOURS = 10;
  const daysWithMinHours = chartData.filter((d) =>
    (d.total || 0) >= MIN_DAILY_HOURS
  ).length;
  const daysWithAnyWear = chartData.filter((d) => d.total > 0).length;
  const pctMinHours  = durationDays ? Math.trunc((daysWithMinHours / durationDays) * 100) : 0;
  const pctDailyWorn = durationDays ? Math.trunc((daysWithAnyWear  / durationDays) * 100) : 0;
  const goalMinHours  = daysWithMinHours === durationDays;
  const goalDailyWorn = daysWithAnyWear  === durationDays;

  // Days with no wear = all calendar days in range minus days where any sensor was worn.
  // This includes days completely absent from all files (within the Messdauer window).
  const daysNoneWornCalendar = durationDays - daysWithAnyWear;

  // Generate every calendar day in the Messdauer window so absent days count as 0h
  const allCalendarDays = [];
  for (let ms = firstMs; ms <= lastMs; ms += msPerDay) {
    const d   = new Date(ms);
    const key = `${String(d.getFullYear()).padStart(4,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    allCalendarDays.push({ key, wd: d.getDay() });
  }

  // Per-sensor weekday averages over the full calendar (absent days = 0h)
  const sensorWeekdayAvgs = sensorStats.map((s) => {
    if (!s) return null;
    const sums   = new Array(7).fill(0);
    const counts = new Array(7).fill(0);
    for (const { key, wd } of allCalendarDays) {
      sums[wd]   += s.dailyHours.get(key) || 0;
      counts[wd] += 1;
    }
    return sums.map((sum, i) => (counts[i] ? sum / counts[i] : null));
  });

  // Combined = sum of corrected per-sensor weekday averages
  const combinedWeekdayAvg = Array.from({ length: 7 }, (_, wd) => {
    let sum = null;
    sensorWeekdayAvgs.forEach((avg) => {
      if (!avg || avg[wd] == null) return;
      sum = (sum ?? 0) + avg[wd];
    });
    return sum;
  });

  return {
    chartData,
    combinedTotalHours,
    combinedSessionCount,
    durationDays,
    avgPerDay,
    daysNoneWorn: daysNoneWornCalendar,
    weekdayAvg: combinedWeekdayAvg,
    sensorWeekdayAvgs,
    pctMinHours,
    pctDailyWorn,
    goalMinHours,
    goalDailyWorn,
  };
}


const CLIP_COLOR = "#e53935";

function StackedBarChart({ data, sensorsPresent, numActiveSensors, displayDecimals }) {
  const [tooltip, setTooltip]   = useState(null);
  const [svgWidth, setSvgWidth] = useState(900);
  const svgContainerRef = useRef(null);

  // Keep viewBox width == container pixel width so scaleX = 1 always — no text distortion
  useEffect(() => {
    const el = svgContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setSvgWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height      = 320;
  const padLeft     = 42;
  const padRight    = 12;
  const padTop      = 12;
  const padBottom   = 30;

  const n = data.length || 1;

  // Enforce a minimum bar slot width so bars stay visible on narrow screens.
  // When the data needs more space than the container provides, the chart
  // becomes wider than its wrapper and the wrapper scrolls horizontally.
  const MIN_BAR_PX  = 3;
  const minChartW   = n * MIN_BAR_PX + padLeft + padRight;
  const width       = Math.max(svgWidth, minChartW);

  const innerWidth  = width  - padLeft - padRight;
  const innerHeight = height - padTop  - padBottom;

  // Hard physical cap: 24 h × number of active sensors.
  // Any day exceeding this is a data anomaly and gets a red warning stripe.
  const yHardMax = 24 * Math.max(1, numActiveSensors);
  const maxTotal = data.reduce((m, d) => Math.max(m, d.total), 0);
  const yMax     = Math.max(26, Math.ceil(Math.min(maxTotal, yHardMax) / 2) * 2);
  const anyClipped = maxTotal > yMax;

  const yStep  = yMax <= 30 ? 2 : Math.ceil(yMax / 13 / 2) * 2;
  const yTicks = [];
  for (let v = 0; v <= yMax; v += yStep) yTicks.push(v);
  const barGap   = innerWidth / n < 6 ? 0.5 : 2;
  const barWidth = Math.max(1, innerWidth / n - barGap);
  const xFor = (i) => padLeft + i * (innerWidth / n);
  const yFor = (v) => padTop + innerHeight - (v / yMax) * innerHeight;

  const firstSunday = data.findIndex((d) => new Date(d.date + "T00:00:00").getDay() === 0);
  const weekLines   = [];
  if (firstSunday >= 0) {
    for (let i = firstSunday; i < n; i += 7) weekLines.push(i);
  }
  // Show a label every N bars where N is chosen so labels are at least 38px apart
  const minPxPerLabel = 38;
  const barSlotWidth  = innerWidth / n;
  const labelStep     = Math.max(1, Math.ceil(minPxPerLabel / barSlotWidth));
  const labelIndices  = new Set();
  for (let i = 0; i < n; i++) {
    if (i % labelStep === 0) labelIndices.add(i);
  }
  // Always include last bar if it's far enough from the previous label
  if (n > 1) {
    const lastShown = [...labelIndices].reduce((m, v) => Math.max(m, v), -1);
    if (n - 1 - lastShown >= labelStep) labelIndices.add(n - 1);
  }

  const showTooltip = (e, d, isClipped) => {
    const rect = svgContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setTooltip({
      x, y,
      flipLeft: x > rect.width * 0.65,
      date: d.date,
      sensors: sensorsPresent.map((present, si) => present ? (d[`s${si + 1}`] ?? 0) : null),
      total: d.total,
      isClipped,
    });
  };

  const moveTooltip = (e) => {
    if (!tooltip) return;
    const rect = svgContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setTooltip((prev) => prev ? { ...prev, x, y, flipLeft: x > rect.width * 0.65 } : null);
  };

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartInner}>
        {/* Y-axis title: HTML element so it never gets distorted by SVG scaling */}
        <div className={styles.yAxisLabel}>Stunden (h)</div>

        {/* SVG + X-axis title share a positioned container for tooltip anchoring */}
        <div className={styles.svgContainer} ref={svgContainerRef}>
          {/* Scroll wrapper: allows the chart to exceed container width on small screens */}
          <div className={styles.svgScrollWrapper}>
          <svg viewBox={`0 0 ${width} ${height}`} className={styles.chartSvg}
            style={{ width: width }} preserveAspectRatio="none"
            onMouseMove={moveTooltip} onMouseLeave={() => setTooltip(null)}>

            {yTicks.map((v) => (
              <g key={v}>
                <line x1={padLeft} x2={width - padRight} y1={yFor(v)} y2={yFor(v)} className={styles.gridLine} />
                <text x={padLeft - 6} y={yFor(v) + 3} className={styles.axisLabel} textAnchor="end">{v}</text>
              </g>
            ))}

            {anyClipped && (
              <line
                x1={padLeft} x2={width - padRight}
                y1={yFor(yMax)} y2={yFor(yMax)}
                stroke={CLIP_COLOR} strokeWidth={1} strokeDasharray="4 3"
              />
            )}

            {weekLines.map((i) => (
              <line key={`w${i}`} x1={xFor(i)} x2={xFor(i)} y1={padTop} y2={height - padBottom} className={styles.weekLine} />
            ))}

            {data.map((d, i) => {
              const isClipped = d.total > yMax;
              const scale = isClipped ? yMax / d.total : 1;
              let yCursor = 0;
              return (
                <g key={d.date}
                  onMouseEnter={(e) => showTooltip(e, d, isClipped)}
                  onMouseMove={(e) => showTooltip(e, d, isClipped)}
                  onMouseLeave={() => setTooltip(null)}
                  style={{ cursor: "default" }}
                >
                  {sensorsPresent.map((present, si) => {
                    if (!present) return null;
                    const val = (d[`s${si + 1}`] ?? 0) * scale;
                    if (!val) return null;
                    const y = yFor(yCursor + val);
                    const h = yFor(yCursor) - y;
                    yCursor += val;
                    return <rect key={si} x={xFor(i)} y={y} width={barWidth} height={h} fill={SENSOR_COLORS[si]} />;
                  })}
                  {isClipped && (
                    <rect x={xFor(i)} y={yFor(yMax)} width={barWidth} height={5} fill={CLIP_COLOR} />
                  )}
                  {labelIndices.has(i) && (
                    <text x={xFor(i) + barWidth / 2} y={height - padBottom + 14} className={styles.axisLabel} textAnchor="middle">
                      {d.date.slice(8, 10)}.{d.date.slice(5, 7)}
                    </text>
                  )}
                </g>
              );
            })}

            <line x1={padLeft} x2={padLeft}          y1={padTop} y2={height - padBottom} className={styles.axisLine} />
            <line x1={padLeft} x2={width - padRight} y1={height - padBottom} y2={height - padBottom} className={styles.axisLine} />
          </svg>
          </div>{/* svgScrollWrapper */}

          {/* X-axis title: HTML element so it never gets distorted by SVG scaling */}
          <div className={styles.xAxisLabel}>Datum</div>

          {tooltip && (
            <div
              className={styles.chartTooltip}
              style={{
                left: tooltip.x + 14,
                top: tooltip.y - 10,
                transform: tooltip.flipLeft ? "translateX(calc(-100% - 28px))" : "none",
              }}
            >
              <div className={styles.tooltipDate}>
                {tooltip.date.slice(8, 10)}.{tooltip.date.slice(5, 7)}.{tooltip.date.slice(0, 4)}
              </div>
              {tooltip.sensors.map((hrs, si) =>
                hrs !== null ? (
                  <div key={si} className={styles.tooltipRow}>
                    <span className={styles.tooltipSwatch} style={{ background: SENSOR_COLORS[si] }} />
                    Sensor {si + 1}: {fmt(hrs, displayDecimals)} h
                  </div>
                ) : null
              )}
              {tooltip.sensors.filter((h) => h !== null).length > 1 && (
                <div className={styles.tooltipTotal}>
                  Gesamt: {fmt(tooltip.total, displayDecimals)} h
                </div>
              )}
              {tooltip.isClipped && (
                <div className={styles.tooltipClipped}>⚠ Gesamt: {fmt(tooltip.total, displayDecimals)} h (abgeschnitten)</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.legend}>
        {SENSOR_LABELS.map(
          (label, i) =>
            sensorsPresent[i] && (
              <span key={label} className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: SENSOR_COLORS[i] }} />
                {label}
              </span>
            )
        )}
        {anyClipped && (
          <span className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: CLIP_COLOR }} />
            Wert abgeschnitten (Datenfehler)
          </span>
        )}
      </div>
    </div>
  );
}

const fmt = (n, digits = 2) => {
  if (!Number.isFinite(n)) return "–";
  const factor = Math.pow(10, digits);
  return (Math.trunc(n * factor) / factor).toFixed(digits);
};

const Orthotimer = () => {
  const navigate = useNavigate();
  const { data: patientData, setData: setPatientData, clearOrthotimer } = useOrthotimer();

  // ── Patient selector state ──────────────────────────────────────
  const [patients, setPatients]               = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm]           = useState("");
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingDetails, setLoadingDetails]   = useState(false);
  const [shopError, setShopError]             = useState(null);
  const [systemError, setSystemError]         = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDropdown, setShowDropdown]       = useState(false);
  const searchRef   = useRef(null);
  const analysesRef = useRef(null);

  const [threshold, setThreshold]       = useState("25");
  const [intervalSec, setIntervalSec]  = useState("300");
  // Enough decimals so 1 sample (intervalSec/3600 h) is never truncated to zero
  const displayDecimals = useMemo(() => {
    const sec = parseInt(String(intervalSec), 10) || 300;
    const sh  = sec / 3600;
    return Math.min(6, Math.max(2, Math.ceil(-Math.log10(sh) + 1)));
  }, [intervalSec]);
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo,   setDateTo]         = useState("");
  const [sensors,       setSensors]       = useState([]);
  const [sensorInput,   setSensorInput]   = useState("");
  const [sensorError,   setSensorError]   = useState("");
  const [slotSerials,   setSlotSerials]   = useState(["", "", ""]);
  const [slots, setSlots]             = useState([{ file: null, key: 0 }, { file: null, key: 0 }, { file: null, key: 0 }]);
  const [results, setResults]         = useState(null);
  const [windowStart, setWindowStart] = useState(0);
  const [showAll, setShowAll]         = useState(false);
  const [isMobile, setIsMobile]       = useState(() => window.innerWidth < 600);
  const [isProcessing, setIsProcessing]   = useState(false);
  const [analyzeError, setAnalyzeError]   = useState("");
  const [analyzeWarning, setAnalyzeWarning] = useState("");

  // Saved orthotimer analyses
  const [orthotimerAnalyses, setOrthotimerAnalyses]     = useState([]);
  const [loadingAnalyses, setLoadingAnalyses]           = useState(false);
  const [selectedAnalysis, setSelectedAnalysis]         = useState(null);
  const [showAnalysesDropdown, setShowAnalysesDropdown] = useState(false);
  const [savingAnalysis, setSavingAnalysis]             = useState(false);
  const [saveError, setSaveError]                       = useState("");
  const [saveSuccess, setSaveSuccess]                   = useState(false);
  const [resultSource, setResultSource]                 = useState(null);

  const MAX_FILE_MB = 5;

  const peekSerial = (text) => {
    const lines = text.split(/\r\n|\n|\r/).filter(Boolean);
    if (lines.length < 2) return null;
    const header = lines[0].split(";").map((h) => h.trim().toLowerCase());
    const idx = header.findIndex((h) => h === "serial");
    if (idx === -1) return null;
    return lines[1].split(";")[idx]?.trim() || null;
  };

  const handleFileChange = async (index, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setAnalyzeError(`Datei "${file.name}": Nur CSV-Dateien (.csv) werden akzeptiert.`);
      return;
    }
    if (file.size === 0) {
      setAnalyzeError(`Datei "${file.name}" ist leer.`);
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setAnalyzeError(`Datei "${file.name}" ist zu groß. Maximale Dateigröße: ${MAX_FILE_MB} MB.`);
      return;
    }
    setSlots((prev) => { const next = [...prev]; next[index] = { file, key: prev[index].key }; return next; });
    setResults(null);
    setAnalyzeError("");

    // Peek serial so the warning can reactively update when sensors are added
    try {
      const text = await readFileAsText(file);
      const serial = peekSerial(text) || "";
      setSlotSerials((prev) => { const next = [...prev]; next[index] = serial; return next; });
    } catch { /* ignore */ }
  };

  const handleRemoveFile = (index) => {
    setSlots((prev) => { const next = [...prev]; next[index] = { file: null, key: prev[index].key + 1 }; return next; });
    setSlotSerials((prev) => { const next = [...prev]; next[index] = ""; return next; });
    setResults(null);
  };

  const handleThresholdChange = (e) => { setThreshold(e.target.value); setResults(null); };

  const handleThresholdBlur = () => {
    const num = parseFloat(String(threshold).replace(",", "."));
    if (Number.isNaN(num)) return;
    if (num < -100) setThreshold("-100");
    else if (num > 100) setThreshold("100");
  };

  const handleReset = () => {
    setSlots((prev) => prev.map((s) => ({ file: null, key: s.key + 1 })));
    setResults(null);
    setAnalyzeError("");
    setAnalyzeWarning("");
    setSlotSerials(["", "", ""]);
    setWindowStart(0);
    setShowAll(false);
    setResultSource(null);
    setSaveSuccess(false);
    setSaveError("");
    setDateFrom("");
    setDateTo("");
  };

  const handleNewAnalysis = () => {
    setSlots((prev) => prev.map((s) => ({ file: null, key: s.key + 1 })));
    setResults(null);
    setAnalyzeError("");
    setAnalyzeWarning("");
    setSlotSerials(["", "", ""]);
    setWindowStart(0);
    setShowAll(false);
    setResultSource(null);
    setSaveSuccess(false);
    setSaveError("");
    setDateFrom("");
    setDateTo("");
    setThreshold("25");
    setIntervalSec("300");
    setSelectedAnalysis(null);
  };

  const handleAnalyze = async () => {
    setAnalyzeError("");
    setAnalyzeWarning("");
    if (!slots[0].file) { setAnalyzeError("Bitte mindestens die Datei für Sensor 1 auswählen."); return; }
    const thresholdNum  = parseFloat(String(threshold).replace(",", "."));
    if (Number.isNaN(thresholdNum)) { setAnalyzeError("Der Schwellenwert ist ungültig."); return; }
    const intervalNum   = parseInt(String(intervalSec), 10);
    if (Number.isNaN(intervalNum) || intervalNum < 1 || intervalNum > 86400) { setAnalyzeError("Das Intervall muss zwischen 1 und 86400 Sekunden liegen."); return; }
    const sampleHours   = intervalNum / 3600;

    if (dateFrom && dateTo && dateFrom > dateTo) {
      setAnalyzeError("Das Startdatum darf nicht nach dem Enddatum liegen.");
      return;
    }

    setIsProcessing(true);
    try {
      const sensorStats = await Promise.all(
        slots.map(async (slot) => {
          if (!slot.file) return null;
          const text = await readFileAsText(slot.file);
          const { rows: allRows, sensorSerial, skippedCount } = parseCsvText(text);

          // Apply date range filter when at least one boundary is set
          const rows = (dateFrom || dateTo)
            ? allRows.filter((r) => {
                if (dateFrom && r.dateKey < dateFrom) return false;
                if (dateTo   && r.dateKey > dateTo)   return false;
                return true;
              })
            : allRows;

          if (!rows.length) throw new Error(
            `Keine Daten in "${slot.file.name}" für den gewählten Zeitraum${dateFrom || dateTo ? ` (${dateFrom || "…"} – ${dateTo || "…"})` : ""} gefunden.`
          );
          return { skippedCount, fileName: slot.file.name, sensorSerial: sensorSerial || "–", ...computeSensorStats(rows, thresholdNum, sampleHours) };
        })
      );

      // Warn about any rows that were skipped due to bad data
      const warnings = sensorStats
        .filter(Boolean)
        .filter((s) => s.skippedCount > 0)
        .map((s) => `"${s.fileName}": ${s.skippedCount} fehlerhafte Zeile(n) übersprungen.`);
      if (warnings.length > 0) setAnalyzeWarning(warnings.join(" "));

      const combined = buildCombined(sensorStats);
      setResults({ sensorStats, combined });
      setWindowStart(0);
      setShowAll(false);
      setResultSource("computed");
      setSaveSuccess(false);
      setSaveError("");
    } catch (err) {
      setAnalyzeError(err.message || "Fehler beim Verarbeiten der Dateien.");
      setResults(null);
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Mobile breakpoint detection ─────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Patient fetch ───────────────────────────────────────────────
  useEffect(() => {
    const fetchPatients = async () => {
      setLoadingPatients(true);
      setShopError(null);
      setSystemError(null);
      try {
        const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
        const jwt = stored?.jwt;
        const swContextToken = stored?.token;
        const testUser = isTestUser(); // TEMPORÄRER TESTZUGANG: no shop session
        if (!jwt || (!swContextToken && !testUser)) {
          setShopError("Authentifizierung fehlgeschlagen. Bitte neu anmelden.");
          setSystemError("Authentifizierung fehlgeschlagen.");
          return;
        }
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwt}`,
          "sw-context-token": swContextToken || "",
        };
        const [shopRes, notShopRes] = await Promise.allSettled([
          testUser
            ? Promise.resolve(null) // TEMPORÄRER TESTZUGANG: skip shop patients
            : fetch(`${process.env.REACT_APP_API_URL}/api/data/patients`, { method: "GET", headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/patientsNotShop?page=1&limit=10000`, { method: "GET", headers }),
        ]);
        let shopPatients = [], notShopPatients = [];
        if (testUser) {
          // TEMPORÄRER TESTZUGANG: no shop patients, no shop error
        } else if (shopRes.status === "fulfilled") {
          const r = await shopRes.value.json();
          if (r.success) shopPatients = r.data || [];
          else setShopError("Die Shop-Patienten konnten nicht geladen werden.");
        } else setShopError("Die Shop-Patienten konnten nicht geladen werden.");
        if (notShopRes.status === "fulfilled") {
          const r = await notShopRes.value.json();
          if (r.success) notShopPatients = r.data || [];
          else setSystemError("Die Systempatienten konnten nicht geladen werden.");
        } else setSystemError("Die Systempatienten konnten nicht geladen werden.");

        const normalize = (p, source) => ({
          patientId: p.patientId || p.patient_encode || p.shop_patient_id || "",
          firstName: p.firstName || p.vorname || "",
          lastName:  p.lastName  || p.name || "",
          birthday:  p.birthday  || p.geburtsdatum || "",
          salutationId: p.salutationId || "",
          source,
        });
        const merged = [
          ...shopPatients.map((p) => normalize(p, "shop")),
          ...notShopPatients.map((p) => normalize(p, "notshop")),
        ];
        setPatients(Array.from(new Map(merged.map((p) => [p.patientId, p])).values()));
      } catch {
        setShopError("Patienten konnten nicht geladen werden.");
      } finally {
        setLoadingPatients(false);
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) { setFilteredPatients(patients); return; }
    const term = searchTerm.toLowerCase().trim();
    setFilteredPatients(patients.filter((p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(term)
    ));
  }, [searchTerm, patients]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
      if (analysesRef.current && !analysesRef.current.contains(e.target)) setShowAnalysesDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Restore selection from context on mount
  useEffect(() => {
    if (patientData.patientId && !selectedPatient) {
      const restored = {
        patientId: patientData.patientId,
        firstName: patientData.firstName,
        lastName:  patientData.lastName,
        birthday:  patientData.birthday,
        source:    patientData.source,
      };
      setSelectedPatient(restored);
      setSearchTerm(`${patientData.firstName} ${patientData.lastName}`.trim());
      fetchOrthotimerAnalyses(patientData.patientId);
      fetchSensors(patientData.patientId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAnalysisById = async (patientId, analysisId) => {
    setLoadingDetails(true);
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientId}/orthotimer/${analysisId}`,
        { method: "GET", headers: { "Content-Type": "application/json", Authorization: `Bearer ${stored?.jwt}`, "sw-context-token": stored?.token } }
      );
      const result = await res.json();
      if (result.success && result.data?.results_json) {
        const json = result.data.results_json;
        setSelectedAnalysis(result.data);
        setResults({
          sensorStats: json.sensors.map((s) =>
            s ? {
              sensorSerial:       s.serial,
              totalHours:         s.totalHours,
              sessionCount:       s.sessionCount,
              morning:            s.morning,
              evening:            s.evening,
              night:              s.night,
              durationDays:       s.durationDays,
              notWornDays:        s.notWornDays,
              wornDays:           s.wornDays,
              avgHoursPerWornDay: s.avgHoursPerWornDay,
              weekdayAvg:         s.weekdayAvg,
            } : null
          ),
          combined: (() => {
            const cd  = json.chartData || [];
            const dur = json.combined.durationDays || cd.length;
            // Use stored values when present (new records); recompute for old records
            const pctMin  = json.combined.pctMinHours  ?? (dur ? Math.trunc((cd.filter((d) => (d.total||0) >= 10).length / dur) * 100) : 0);
            const pctDay  = json.combined.pctDailyWorn ?? (dur ? Math.trunc((cd.filter((d) => (d.total||0) >  0).length / dur) * 100) : 0);
            return {
              chartData:            cd,
              combinedTotalHours:   json.combined.totalHours,
              combinedSessionCount: json.combined.sessionCount,
              durationDays:         dur,
              avgPerDay:            json.combined.avgPerDay,
              daysNoneWorn:         json.combined.daysNoneWorn,
              weekdayAvg:           json.combined.weekdayAvg,
              sensorWeekdayAvgs:    json.combined.sensorWeekdayAvgs ?? null,
              pctMinHours:          pctMin,
              pctDailyWorn:         pctDay,
              goalMinHours:         json.combined.goalMinHours  ?? (pctMin  === 100 && dur > 0),
              goalDailyWorn:        json.combined.goalDailyWorn ?? (pctDay  === 100 && dur > 0),
            };
          })(),
        });
        setThreshold(String(json.threshold));
        setIntervalSec(String(json.intervalSec ?? 300));
        setDateFrom(result.data.date_from || "");
        setDateTo(result.data.date_to   || "");
        setWindowStart(0);
        setShowAll(false);
        setResultSource("loaded");
        setSaveError("");
        setSaveSuccess(false);
      }
    } catch { setSaveError("Auswertung konnte nicht geladen werden."); } finally { setLoadingDetails(false); }
  };

  const fetchOrthotimerAnalyses = async (patientId) => {
    if (!patientId) return;
    setLoadingAnalyses(true);
    setOrthotimerAnalyses([]);
    setSelectedAnalysis(null);
    setShowAnalysesDropdown(false);
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientId}/orthotimer`,
        { method: "GET", headers: { "Content-Type": "application/json", Authorization: `Bearer ${stored?.jwt}`, "sw-context-token": stored?.token } }
      );
      const result = await res.json();
      if (result.success) {
        const list = result.data || [];
        setOrthotimerAnalyses(list);
        if (list.length > 0) {
          const latest = list[list.length - 1];
          await loadAnalysisById(patientId, latest.id);
        }
      }
    } catch { /* ignore */ } finally { setLoadingAnalyses(false); }
  };

  const authHeaders = () => {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    return { "Content-Type": "application/json", Authorization: `Bearer ${stored?.jwt}`, "sw-context-token": stored?.token };
  };

  const fetchSensors = async (patientId) => {
    if (!patientId) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/patients/${patientId}/sensors`, { method: "GET", headers: authHeaders() });
      const result = await res.json();
      if (result.success) setSensors(result.data || []);
    } catch { /* ignore */ }
  };

  const handleAddSensor = async () => {
    const serial = sensorInput.trim();
    if (!serial) { setSensorError("Bitte eine Seriennummer eingeben."); return; }
    setSensorError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${selectedPatient.patientId}/sensors`,
        { method: "POST", headers: authHeaders(), body: JSON.stringify({ serial_number: serial, source: selectedPatient.source }) }
      );
      const result = await res.json();
      if (result.success) { setSensors((prev) => [...prev, result.data]); setSensorInput(""); }
      else setSensorError("Sensor konnte nicht gespeichert werden.");
    } catch { setSensorError("Fehler beim Speichern des Sensors."); }
  };

  const handleToggleSensorStatus = async (sensor) => {
    const newStatus = sensor.status === "working" ? "defect" : "working";
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${selectedPatient.patientId}/sensors/${sensor.id}`,
        { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: newStatus }) }
      );
      const result = await res.json();
      if (result.success) setSensors((prev) => prev.map((s) => (s.id === sensor.id ? { ...s, status: newStatus } : s)));
    } catch { /* ignore */ }
  };

  const handleDeleteSensor = async (sensorId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${selectedPatient.patientId}/sensors/${sensorId}`,
        { method: "DELETE", headers: authHeaders() }
      );
      const result = await res.json();
      if (result.success) setSensors((prev) => prev.filter((s) => s.id !== sensorId));
    } catch { /* ignore */ }
  };

  const handleLoadAnalysis = async (analysisId) => {
    if (!analysisId || !selectedPatient) return;
    setShowAnalysesDropdown(false);
    await loadAnalysisById(selectedPatient.patientId, analysisId);
  };

  const handleSaveAnalysis = async () => {
    if (!results || !selectedPatient) return;
    setSavingAnalysis(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const thresholdNum = parseFloat(String(threshold).replace(",", "."));
      const payload = {
        threshold:            thresholdNum,
        source:               selectedPatient.source,
        csv_file_names:       slots.filter((s) => s.file).map((s) => s.file.name),
        date_from:            results.combined.chartData[0]?.date || null,
        date_to:              results.combined.chartData[results.combined.chartData.length - 1]?.date || null,
        combined_total_hours: results.combined.combinedTotalHours,
        results_json: {
          threshold: thresholdNum,
          intervalSec: parseInt(String(intervalSec), 10) || 300,
          sensors: results.sensorStats.map((s) =>
            s ? {
              serial:             s.sensorSerial,
              totalHours:         s.totalHours,
              sessionCount:       s.sessionCount,
              morning:            s.morning,
              evening:            s.evening,
              night:              s.night,
              durationDays:       s.durationDays,
              notWornDays:        s.notWornDays,
              wornDays:           s.wornDays,
              avgHoursPerWornDay: s.avgHoursPerWornDay,
              weekdayAvg:         s.weekdayAvg,
            } : null
          ),
          combined: {
            totalHours:        results.combined.combinedTotalHours,
            sessionCount:      results.combined.combinedSessionCount,
            durationDays:      results.combined.durationDays,
            avgPerDay:         results.combined.avgPerDay,
            daysNoneWorn:      results.combined.daysNoneWorn,
            weekdayAvg:        results.combined.weekdayAvg,
            sensorWeekdayAvgs: results.combined.sensorWeekdayAvgs,
            pctMinHours:       results.combined.pctMinHours,
            pctDailyWorn:      results.combined.pctDailyWorn,
            goalMinHours:      results.combined.goalMinHours,
            goalDailyWorn:     results.combined.goalDailyWorn,
          },
          chartData: results.combined.chartData,
        },
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/patients/${selectedPatient.patientId}/orthotimer`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${stored?.jwt}`, "sw-context-token": stored?.token },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (result.success) {
        setSaveSuccess(true);
        setResultSource("loaded");
        await fetchOrthotimerAnalyses(selectedPatient.patientId);
      } else {
        setSaveError("Auswertung konnte nicht gespeichert werden.");
      }
    } catch {
      setSaveError("Netzwerkfehler beim Speichern.");
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleSelect = (patient) => {
    if (!patient) return;
    const firstName = patient.firstName || "";
    const lastName  = patient.lastName  || "";
    const geschlecht = mapSalutationToGeschlecht(patient.salutationId);
    setSelectedPatient(patient);
    setSearchTerm(`${firstName} ${lastName}`.trim());
    setPatientData((prev) => ({
      ...prev,
      patientId: patient.patientId,
      firstName,
      lastName,
      birthday: patient.birthday || "",
      source:   patient.source,
      ...(geschlecht !== null && { geschlecht }),
    }));
    fetchOrthotimerAnalyses(patient.patientId);
    fetchSensors(patient.patientId);
    setShowDropdown(false);
  };

  const clearPatientSelection = () => {
    setSelectedPatient(null);
    setSearchTerm("");
    setOrthotimerAnalyses([]);
    setSelectedAnalysis(null);
    setSaveError("");
    setSaveSuccess(false);
    setResultSource(null);
    setResults(null);
    setDateFrom("");
    setDateTo("");
    setSensors([]);
    setSensorInput("");
    setSensorError("");
    setSlotSerials(["", "", ""]);
    clearOrthotimer();
  };

  const sensorsPresent = useMemo(
    () => (results ? results.sensorStats.map(Boolean) : [false, false, false]),
    [results]
  );

  const slotWarnings = useMemo(
    () => slotSerials.map((serial) => {
      if (!serial || sensors.length === 0) return "";
      return sensors.some((s) => s.serial_number === serial)
        ? ""
        : `Achtung: Sensor „${serial}" ist nicht für diesen Patienten registriert.`;
    }),
    [slotSerials, sensors]
  );

  const WINDOW      = isMobile ? 14 : 30;
  const WINDOW_ALL  = isMobile ? 60 : 180;

  const activeWindow = showAll ? WINDOW_ALL : WINDOW;

  const filteredChartData = useMemo(() => {
    if (!results) return [];
    const data = results.combined.chartData;
    if (data.length <= activeWindow) return data;
    const end   = Math.min(windowStart + activeWindow, data.length);
    const start = Math.max(0, end - activeWindow);
    return data.slice(start, end);
  }, [results, windowStart, activeWindow]);

  return (
    <div className={styles.sensorPage}>
      <h1>Orthotimer – Auswertung</h1>

      {/* ── Patientenauswahl ────────────────────────────── */}
      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Patientenliste</h2>

        <div className={styles["search-container"]} ref={searchRef}>
          <div className={styles["search-wrapper"]}>
            <input
              type="text"
              placeholder="Vor- oder Nachname eingeben..."
              className={styles["search-input"]}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              disabled={loadingPatients}
            />
            {searchTerm && (
              <button className={styles["clear-btn"]} onClick={clearPatientSelection}>✕</button>
            )}
          </div>

          {showDropdown && !loadingPatients && (
            <div className={styles["dropdown-list"]}>
              {filteredPatients.length > 0 ? filteredPatients.map((p) => (
                <div
                  key={p.patientId}
                  className={styles["dropdown-item"]}
                  onClick={() => handleSelect(p)}
                >
                  <div className={styles["dropdown-item-content"]}>
                    {p.source === "notshop" && !isTestUser() /* TEMPORÄRER TESTZUGANG */ && <span className={styles["notshop-circle"]}>●</span>}
                    <div>
                      <strong>{p.firstName} {p.lastName}</strong>
                      <span> — {p.patientId || "—"}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className={styles["dropdown-item"]}>Keine Patienten gefunden</div>
              )}
            </div>
          )}
        </div>

        {selectedPatient && (
          <div className={styles["versions-section"]}>
            <p className={styles["versions-label"]}>Gespeicherte Auswertungen</p>
            {loadingAnalyses ? (
              <p className={styles.loading}>Auswertungen werden geladen...</p>
            ) : orthotimerAnalyses.length === 0 ? (
              <p className={styles["versions-empty"]}>Keine gespeicherten Auswertungen</p>
            ) : (
              <div className={styles["versions-select-container"]} ref={analysesRef}>
                <div
                  className={styles["versions-select"]}
                  onClick={() => setShowAnalysesDropdown((v) => !v)}
                >
                  {selectedAnalysis
                    ? (() => {
                        const d = new Date(selectedAnalysis.created_at);
                        const f = d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                        return `${f} — ${(selectedAnalysis.combined_total_hours ?? 0).toFixed(1)} h, Schwelle: ${selectedAnalysis.threshold}°C`;
                      })()
                    : "Auswertungen"}
                </div>
                {showAnalysesDropdown && (
                  <div className={styles["dropdown-list"]}>
                    {orthotimerAnalyses.map((a) => {
                      const d = new Date(a.created_at);
                      const f = d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                      return (
                        <div key={a.id} className={styles["dropdown-item"]} onClick={() => handleLoadAnalysis(a.id)}>
                          {f} — {(a.combined_total_hours ?? 0).toFixed(1)} h, Schwelle: {a.threshold}°C
                          {a.date_from && a.date_to && ` (${a.date_from} – ${a.date_to})`}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loadingPatients  && <p className={styles.loading}>Patienten werden geladen...</p>}
        {loadingDetails   && <p className={styles.loading}>Patientendetails werden geladen...</p>}
        {shopError        && <p className={styles["error-text"]}>{shopError}</p>}
        {systemError      && <p className={styles["error-text"]}>{systemError}</p>}
      </section>

      {/* ── Sensorzuordnung ──────────────────────────── */}
      {selectedPatient && (
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>Sensorzuordnung</h2>

          <div className={styles.sensorAddRow}>
            <input
              type="text"
              className={styles.sensorSerialInput}
              placeholder="Seriennummer eingeben…"
              value={sensorInput}
              onChange={(e) => { setSensorInput(e.target.value); setSensorError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleAddSensor()}
            />
            <button type="button" className={styles.primaryBtn} onClick={handleAddSensor}>
              Hinzufügen
            </button>
          </div>
          {sensorError && <p className={styles.errorText}>{sensorError}</p>}

          {sensors.length === 0 ? (
            <p className={styles.sensorEmpty}>Noch keine Sensoren für diesen Patienten erfasst.</p>
          ) : (
            <ul className={styles.sensorList}>
              {sensors.map((s) => (
                <li key={s.id} className={styles.sensorItem}>
                  <span className={styles.sensorSerial}>{s.serial_number}</span>
                  <span className={s.status === "working" ? styles.badgeWorking : styles.badgeDefect}>
                    {s.status === "working" ? "Funktionsfähig" : "Defekt"}
                  </span>
                  <button
                    type="button"
                    className={styles.sensorToggleBtn}
                    onClick={() => handleToggleSensorStatus(s)}
                  >
                    {s.status === "working" ? "Als defekt markieren" : "Als funktionsfähig markieren"}
                  </button>
                  <button
                    type="button"
                    className={styles.sensorDeleteBtn}
                    onClick={() => handleDeleteSensor(s.id)}
                    title="Sensor entfernen"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── Sensordaten ────────────────────────────────── */}
      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Sensordaten (CSV)</h2>
        {resultSource === "loaded" ? (
          <div className={styles.thresholdRow}>
            <div className={styles.thresholdGroup}>
              <span className={styles.label}>Schwellenwert (°C)</span>
              <span className={styles.readonlyValue}>{threshold}</span>
            </div>
            <div className={styles.thresholdGroup}>
              <span className={styles.label}>Intervall (s)</span>
              <span className={styles.readonlyValue}>{intervalSec}</span>
            </div>
            <div className={styles.thresholdGroup}>
              <span className={styles.label}>Von</span>
              <span className={styles.readonlyValue}>{dateFrom || "–"}</span>
            </div>
            <div className={styles.thresholdGroup}>
              <span className={styles.label}>Bis</span>
              <span className={styles.readonlyValue}>{dateTo || "–"}</span>
            </div>
          </div>
        ) : (
          <div className={styles.thresholdRow}>
            <div className={styles.thresholdGroup}>
              <label className={styles.label}>Schwellenwert (°C)</label>
              <input
                type="number"
                className={styles.thresholdInput}
                value={threshold}
                onChange={handleThresholdChange}
                min="-100"
                max="100"
                onBlur={handleThresholdBlur}
              />
            </div>
            <div className={styles.thresholdGroup}>
              <label className={styles.label}>Intervall (s)</label>
              <input
                type="number"
                className={styles.thresholdInput}
                value={intervalSec}
                onChange={(e) => { setIntervalSec(e.target.value); setResults(null); }}
                onBlur={() => {
                  const n = parseInt(String(intervalSec), 10);
                  if (Number.isNaN(n) || n < 1) setIntervalSec("1");
                  else if (n > 86400) setIntervalSec("86400");
                }}
                min="1"
                max="86400"
              />
            </div>
            <div className={styles.thresholdGroup}>
              <label className={styles.label}>Von</label>
              <input
                type="date"
                className={styles.dateInput}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className={styles.thresholdGroup}>
              <label className={styles.label}>Bis</label>
              <input
                type="date"
                className={styles.dateInput}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        )}
        <p className={styles.thresholdHint}>
          Temperaturgrenzwert zur Erkennung der Tragezeit. Messwerte oberhalb dieses Werts gelten als „Schuh getragen" (Standard: 25 °C).
        </p>
        {resultSource !== "loaded" && (
          <>
            <p className={styles.fileHint}>Maximale Dateigröße: {MAX_FILE_MB} MB pro Datei.</p>
            <div className={styles.row}>
              {SENSOR_LABELS.map((label, i) => (
                <div className={styles.col} key={label}>
                  <label className={styles.label}>{label}{i === 0 ? " (erforderlich)" : " (optional)"}</label>
                  <div className={styles.fileInputRow}>
                    <input
                      key={slots[i].key}
                      type="file"
                      accept=".csv,text/csv"
                      className={styles.fileInput}
                      onChange={(e) => handleFileChange(i, e.target.files[0] || null)}
                    />
                    {slots[i].file && (
                      <button type="button" className={styles.removeFileBtn} onClick={() => handleRemoveFile(i)}>✕</button>
                    )}
                  </div>
                  {slots[i].file && <p className={styles.fileName}>{slots[i].file.name}</p>}
                  {slotWarnings[i] && <p className={styles.slotWarning}>{slotWarnings[i]}</p>}
                </div>
              ))}
            </div>
            {analyzeError   && <p className={styles.errorText}>{analyzeError}</p>}
            {analyzeWarning && <p className={styles.warningText}>{analyzeWarning}</p>}
          </>
        )}

        {resultSource === "loaded" ? (
          <div className={styles.actionsRow}>
            <button type="button" className={styles.newAnalysisBtn} onClick={handleNewAnalysis}>
              + Neue Auswertung erstellen
            </button>
          </div>
        ) : (
          <div className={styles.actionsRow}>
            <button type="button" className={styles.secondaryBtn} onClick={handleReset}>Dateien zurücksetzen</button>
            <button type="button" className={styles.primaryBtn} onClick={handleAnalyze} disabled={isProcessing}>
              {isProcessing ? "Wird verarbeitet…" : "Daten auswerten"}
            </button>
            {results && resultSource === "computed" && selectedPatient && (
              <button type="button" className={styles.primaryBtn} onClick={handleSaveAnalysis} disabled={savingAnalysis}>
                {savingAnalysis ? "Wird gespeichert…" : "Auswertung speichern"}
              </button>
            )}
          </div>
        )}
        {saveSuccess && <p className={styles["save-success"]}>Auswertung erfolgreich gespeichert.</p>}
        {saveError   && <p className={styles.errorText}>{saveError}</p>}
      </section>

      {results && (
        <>
          {/* ── Ergebnisse ─────────────────────────────── */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>Ergebnisse</h2>
            {resultSource === "loaded" && selectedAnalysis && (
              <p className={styles["loaded-badge"]}>
                Gespeicherte Auswertung vom{" "}
                {new Date(selectedAnalysis.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            <div className={styles.summaryGrid}>
              {results.sensorStats.map(
                (s, i) =>
                  s && (
                    <div className={styles.summaryCard} key={i}>
                      <h3>{SENSOR_LABELS[i]}</h3>
                      <p className={styles.statLine}>
                        <span className={styles.statLabel}>Sensornummer</span>
                        <span className={styles.statValueSerial} title={s.sensorSerial}>{s.sensorSerial}</span>
                      </p>
                      <p className={styles.statLine}>
                        <span className={styles.statLabel}>Tragezeit gesamt</span>
                        <span className={styles.statValue}>{fmt(s.totalHours, displayDecimals)} h</span>
                      </p>
                      {/* Schuh Tragehäufigkeit hidden — too volatile with short intervals
                      <p className={styles.statLine}>
                        <span className={styles.statLabel}>Schuh Tragehäufigkeit</span>
                        <span className={styles.statValue}>{s.sessionCount}</span>
                      </p> */}
                      <p className={styles.statLine}>
                        <span className={styles.statLabel}>Mittlere Tragezeit pro Tag</span>
                        <span className={styles.statValue}>{fmt(s.totalHours / results.combined.durationDays, displayDecimals)} h</span>
                      </p>
                      <p className={styles.statLine}>
                        <span className={styles.statLabel}>Nicht getragen</span>
                        <span className={styles.statValue}>{results.combined.durationDays - s.wornDays} Tage</span>
                      </p>
                    </div>
                  )
              )}

              <div className={`${styles.summaryCard} ${styles.summaryCardCombined}`}>
                <h3>Gesamt (alle Sensoren)</h3>
                <p className={styles.statLine}>
                  <span className={styles.statLabel}>Messdauer</span>
                  <span className={styles.statValue}>{results.combined.durationDays} Tage</span>
                </p>
                <p className={styles.statLine}>
                  <span className={styles.statLabel}>Tragezeit gesamt</span>
                  <span className={styles.statValue}>{fmt(results.combined.combinedTotalHours, displayDecimals)} h</span>
                </p>
                {/* Schuh Tragehäufigkeit gesamt hidden — too volatile with short intervals
                <p className={styles.statLine}>
                  <span className={styles.statLabel}>Schuh Tragehäufigkeit gesamt</span>
                  <span className={styles.statValue}>{results.combined.combinedSessionCount}</span>
                </p> */}
                <p className={styles.statLine}>
                  <span className={styles.statLabel}>Kein Schuh getragen</span>
                  <span className={styles.statValue}>{results.combined.daysNoneWorn} Tage</span>
                </p>
                <p className={styles.statLineHighlight}>
                  <span className={styles.statLabelHighlight}>Mittlere Tragezeit pro Tag</span>
                  <span className={styles.statValueHighlight}>{fmt(results.combined.avgPerDay, displayDecimals)} h</span>
                </p>

              </div>
            </div>

            <div className={styles.goalsRow}>
              <div className={results.combined.goalMinHours ? styles.goalCardYes : styles.goalCardNo}>
                <span className={styles.goalCardLabel}>≥ 10 h/Tag getragen</span>
                <span className={styles.goalCardBadge}>
                  {results.combined.goalMinHours ? "Ja" : "Nein"}
                </span>
                <span className={styles.goalCardPct}>{results.combined.pctMinHours} % der Tage</span>
              </div>
              <div className={results.combined.goalDailyWorn ? styles.goalCardYes : styles.goalCardNo}>
                <span className={styles.goalCardLabel}>Jeden Tag getragen</span>
                <span className={styles.goalCardBadge}>
                  {results.combined.goalDailyWorn ? "Ja" : "Nein"}
                </span>
                <span className={styles.goalCardPct}>{results.combined.pctDailyWorn} % der Tage</span>
              </div>
            </div>
          </section>

          {/* ── Tageszeit-Verteilung ───────────────────── */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>Tageszeit-Verteilung</h2>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Zeitraum</th>
                    {results.sensorStats.map((s, i) => s && <th key={i}>{SENSOR_LABELS[i]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Tagsüber (7–17 Uhr)", "morning"],
                    ["Abends (17–22 Uhr)", "evening"],
                    ["Nachts (22–7 Uhr)",  "night"],
                  ].map(([label, key]) => (
                    <tr key={key}>
                      <td>{label}</td>
                      {results.sensorStats.map((s, i) => s && <td key={i}>{fmt(s[key], displayDecimals)} h</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Wochentage ─────────────────────────────── */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>Durchschnittliche Tragezeit pro Wochentag</h2>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Wochentag</th>
                    {results.sensorStats.map((s, i) => s && <th key={i}>{SENSOR_LABELS[i]}</th>)}
                    <th>Kombiniert</th>
                  </tr>
                </thead>
                <tbody>
                  {WEEKDAY_NAMES.map((name, idx) => {
                    const wd = WEEKDAY_ORDER[idx];
                    return (
                      <tr key={name}>
                        <td>{name}</td>
                        {results.sensorStats.map(
                          (s, i) => {
                            if (!s) return null;
                            const avg = results.combined.sensorWeekdayAvgs?.[i]?.[wd] ?? s.weekdayAvg[wd];
                            return (
                              <td key={i}>
                                {avg != null ? `${fmt(avg, displayDecimals)} h` : "–"}
                              </td>
                            );
                          }
                        )}
                        <td>
                          {results.combined.weekdayAvg[wd] != null
                            ? `${fmt(results.combined.weekdayAvg[wd], displayDecimals)} h`
                            : "–"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Tagesdiagramm ──────────────────────────── */}
          <section className={styles.card}>
            {(() => {
              const totalDays = results.combined.chartData.length;
              const needsNav  = totalDays > activeWindow;
              const dateLabel = filteredChartData.length > 0
                ? `${filteredChartData[0].date.slice(8,10)}.${filteredChartData[0].date.slice(5,7)} – ${filteredChartData[filteredChartData.length-1].date.slice(8,10)}.${filteredChartData[filteredChartData.length-1].date.slice(5,7)}`
                : "";
              return (
                <div className={styles.chartHeader}>
                  <h2 className={styles.sectionTitle}>Tragezeit pro Tag</h2>
                  <div className={styles.chartNav}>
                    {needsNav && (
                      <>
                        <button
                          className={styles.chartNavBtn}
                          onClick={() => setWindowStart((s) => Math.max(0, s - activeWindow))}
                          disabled={windowStart === 0}
                        >‹</button>
                        <span className={styles.chartNavLabel}>{dateLabel}</span>
                        <button
                          className={styles.chartNavBtn}
                          onClick={() => setWindowStart((s) => Math.min(totalDays - activeWindow, s + activeWindow))}
                          disabled={windowStart + activeWindow >= totalDays}
                        >›</button>
                      </>
                    )}
                    {totalDays > WINDOW && (
                      <button className={styles.chartNavBtn} onClick={() => { setShowAll((v) => !v); setWindowStart(0); }}>
                        {showAll ? "Fensteransicht" : "Übersicht"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            <StackedBarChart
              data={filteredChartData}
              sensorsPresent={sensorsPresent}
              numActiveSensors={sensorsPresent.filter(Boolean).length}
              displayDecimals={displayDecimals}
            />
          </section>

        </>
      )}

      <div className={styles.actionsRow}>
        <button type="button" className={styles.primaryBtn} onClick={() => navigate(-1)}>Zurück</button>
      </div>
    </div>
  );
};

export default Orthotimer;
